import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { CtaBand } from "@/components/marketing/CtaBand";
import { findRole, CATEGORIES } from "@/app/marketing/constants/rolesCatalog";
import { usePageMeta } from "./usePageMeta";

export function RoleDetail() {
  const { code } = useParams<{ code: string }>();
  const role = code ? findRole(code) : undefined;
  usePageMeta({
    title: role ? `${role.name} — virtual agent` : "Role not found",
    description: role
      ? `${role.summary} — ${role.description.slice(0, 140)}`
      : "This role isn't in our catalog yet.",
  });

  if (!role) {
    return (
      <SectionContainer>
        <div className="max-w-2xl">
          <h1 className="font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            Role not found.
          </h1>
          <p className="mt-4 text-base text-brand-ink/70">
            That role doesn't exist in our catalog yet. Browse the full list or tell us what you're
            looking for.
          </p>
          <div className="mt-6 flex gap-3">
            <Button asChild variant="primary">
              <Link to="/features">Browse all roles</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-brand-ink hover:bg-white/5">
              <Link to="/contact">Request a role</Link>
            </Button>
          </div>
        </div>
      </SectionContainer>
    );
  }

  const Icon = role.icon;
  const catLabel = CATEGORIES.find((c) => c.id === role.category)?.label ?? role.category;
  const hireHref = `/register?role=${encodeURIComponent(role.code)}`;

  return (
    <>
      <SectionContainer>
        <Link
          to="/features"
          className="inline-flex items-center gap-1.5 text-sm text-brand-ink/60 hover:text-brand-ink transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          All roles
        </Link>

        <div className="mt-8 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-start">
          <div>
            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider">
              <span className="text-brand-violet">{catLabel}</span>
              <span className="text-brand-ink/30">·</span>
              <span className="font-mono text-brand-ink/60">{role.code}</span>
            </div>
            <div className="mt-5 flex items-start gap-5">
              <div className="rounded-xl bg-brand-violet/15 p-4 text-brand-violet shrink-0">
                <Icon className="h-10 w-10" />
              </div>
              <div>
                <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-[0.95] text-brand-ink">
                  {role.name}
                </h1>
                <p className="mt-3 text-lg text-brand-ink/70">{role.summary}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-brand-violet/30 bg-gradient-to-b from-brand-violet/10 to-transparent p-6 w-full lg:w-64">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/60">
              Starts at
            </p>
            <p className="mt-2 font-display text-4xl text-brand-ink">{role.startingPrice}</p>
            <p className="mt-1 text-xs text-brand-ink/60">per agent, per month</p>
            <Button asChild variant="primary" className="mt-5 w-full">
              <Link to={hireHref}>
                Hire this agent
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12">
          <div>
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-brand-ink">
              What they do
            </h2>
            <p className="mt-4 text-base md:text-lg text-brand-ink/75 leading-relaxed">
              {role.description}
            </p>

            <h3 className="mt-10 text-sm font-semibold uppercase tracking-wider text-brand-ink/60">
              Capabilities
            </h3>
            <ul className="mt-4 space-y-3">
              {role.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-violet shrink-0 mt-0.5" />
                  <span className="text-base text-brand-ink/85">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-ink/60">
              Supported tools
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {role.tools.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-sm font-medium text-brand-ink/85"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-ink/60">
                Tenant scoping
              </h4>
              <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">
                This agent runs scoped to your workspace only. It cannot read data from other
                tenants. Every tool call is audit-logged.
              </p>
            </div>

            <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-ink/60">
                Human approvals
              </h4>
              <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">
                High-blast-radius actions (merging code, sending external email, refunds) queue for
                human review. Thresholds are configurable in workspace settings.
              </p>
            </div>
          </div>
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl md:text-4xl tracking-wide text-brand-ink">
            Example workflows
          </h2>
          <p className="mt-3 text-base text-brand-ink/70">
            Two of the common scenarios where this role pays for itself in the first week.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {role.workflows.map((w) => (
            <div
              key={w.title}
              className="rounded-xl border border-white/10 bg-white/5 p-6"
            >
              <h3 className="text-lg font-semibold text-brand-ink">{w.title}</h3>
              <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{w.description}</p>
            </div>
          ))}
        </div>
      </SectionContainer>

      <CtaBand
        heading={`Ready to hire a ${role.name}?`}
        subheading="Spin up your workspace and configure your first agent in under two minutes."
        primaryLabel="Hire this agent"
        primaryHref={hireHref}
        secondaryLabel="Talk to sales"
        secondaryHref="/contact"
      />
    </>
  );
}
